import React, { useState, useEffect, useRef } from "react";
import { programFlow, siteData } from "../data/mock";

const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.1, ...options });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [ref, isVisible];
};

const mono = "'JetBrains Mono', monospace";
const sans = "'Inter', Arial, sans-serif";
const heading = "'Montserrat', Arial, sans-serif";

const timelineItems = [
  {
    ...programFlow[0],
    description: "Arrive early, find your seat, and stop by our interactive kiosks",
    duration: "30 min",
    type: "standard",
  },
  {
    ...programFlow[1],
    description: "Welcome remarks and event kickoff by your MC",
    duration: "15 min",
    type: "opening",
  },
  {
    ...programFlow[2],
    description: "Meet the speakers and set the stage for the afternoon",
    duration: "15 min",
    type: "standard",
  },
  {
    ...programFlow[3],
    description: "Powerful personal narratives from three accomplished leaders",
    duration: "45 min",
    type: "keynote",
  },
  {
    ...programFlow[4],
    description: "Networking, sponsor activations, and refreshments",
    duration: "30 min",
    type: "break",
  },
  {
    ...programFlow[5],
    description: "Moderated conversation exploring leadership and resilience",
    duration: "30 min",
    type: "panel",
  },
  {
    ...programFlow[6],
    description: "Direct engagement and questions with the speakers",
    duration: "30 min",
    type: "standard",
  },
  {
    ...programFlow[7],
    description: "Final words of inspiration and a call to action",
    duration: null,
    type: "closing",
  },
];

const isHighlight = (type) => type === "keynote" || type === "panel" || type === "opening";

export const EventFormatSection = () => {
  const [headerRef, headerVisible] = useInView();
  const [timelineRef, timelineVisible] = useInView();

  return (
    <section
      id="schedule"
      className="py-16 md:py-[120px] relative overflow-hidden"
      style={{
        background: "#fafbfc",
        backgroundImage: "radial-gradient(circle, #e5e7eb 1px, transparent 1px)",
        backgroundSize: "24px 24px",
      }}
    >
      <div className="max-w-[87.5rem] mx-auto px-6 md:px-10">
        {/* Section Header */}
        <div
          ref={headerRef}
          className="mb-10 md:mb-16 transition-all duration-700"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? "translateY(0)" : "translateY(30px)",
          }}
        >
          <span
            className="block text-[#9ca3af] text-sm mb-4"
            style={{ fontFamily: mono }}
          >
            // Schedule
          </span>
          <h2
            className="text-[#FE3A80] uppercase leading-[0.85] mb-4"
            style={{
              fontFamily: heading,
              fontWeight: 900,
              fontSize: "clamp(2rem, 1.5rem + 2vw, 3rem)",
            }}
          >
            Event Format
          </h2>
          <p
            className="text-[#4b5563] text-lg font-medium leading-relaxed max-w-2xl"
            style={{ fontFamily: sans }}
          >
            Three hours of inspiration, connection, and unforgettable moments.
          </p>
        </div>

        {/* Logistics Row */}
        <div
          className="grid grid-cols-1 md:grid-cols-3 border border-[#e5e7eb] bg-white mb-12 md:mb-16 transition-all duration-500"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? "translateY(0)" : "translateY(16px)",
            transitionDelay: headerVisible ? "200ms" : "0ms",
          }}
        >
          {[
            { label: "Date", value: siteData.date },
            { label: "Time", value: "1:00 – 4:00 PM" },
            { label: "Location", value: siteData.location, href: "https://www.google.com/maps?gs_lcrp=EgZjaHJvbWUqDQgBEC4YrwEYxwEYgAQyCggAEEUYFhgeGDkyDQgBEC4YrwEYxwEYgAQyBwgCEAAYgAQyBwgDEAAYgAQyCAgEEAAYFhgeMggIBRAAGBYYHjIICAYQABgWGB4yCAgHEAAYFhgeMggICBAAGBYYHtIBCDM1OTFqMGo3qAIAsAIA&um=1&ie=UTF-8&fb=1&gl=ca&sa=X&geocode=KQEAAASqBc5MMe-h3NlgfxTY&daddr=245+Centrum+Blvd,+Orl%C3%A9ans,+ON+K1E+0A1" },
          ].map((item, i) => (
            <div
              key={i}
              className={`p-4 md:p-6 text-center ${i > 0 ? "border-t md:border-t-0 md:border-l border-[#e5e7eb]" : ""}`}
            >
              <span
                className="text-[#9ca3af] text-[10px] md:text-[11px] uppercase tracking-[0.2em] font-medium block mb-1"
                style={{ fontFamily: sans }}
              >
                {item.label}
              </span>
              {item.href ? (
                <a
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#1f2937] text-sm md:text-lg font-bold block underline hover:text-[#FE3A80] transition-colors"
                  style={{ fontFamily: heading, fontWeight: 800 }}
                >
                  {item.value}
                </a>
              ) : (
                <span
                  className="text-[#1f2937] text-sm md:text-lg font-bold block"
                  style={{ fontFamily: heading, fontWeight: 800 }}
                >
                  {item.value}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* ── Desktop Timeline ── */}
        <div ref={timelineRef} className="hidden md:block relative max-w-[52rem] mx-auto">
          {/* Center vertical line */}
          <div
            className="absolute top-0 bottom-0 w-[2px] transition-all duration-1000"
            style={{
              left: 119,
              background: timelineVisible
                ? "linear-gradient(to bottom, #FE3A80 0%, #FE3A80 70%, #e5e7eb 100%)"
                : "#e5e7eb",
            }}
          />

          {timelineItems.map((item, i) => {
            const highlight = isHighlight(item.type);
            const isBreak = item.type === "break";
            const isClosing = item.type === "closing";

            return (
              <div
                key={i}
                className="relative flex items-stretch group"
                style={{
                  marginBottom: i < timelineItems.length - 1 ? 0 : 0,
                  opacity: timelineVisible ? 1 : 0,
                  transform: timelineVisible ? "translateY(0)" : "translateY(24px)",
                  transition: "opacity 600ms, transform 600ms",
                  transitionDelay: timelineVisible ? `${i * 100}ms` : "0ms",
                }}
              >
                {/* Left: Time column */}
                <div
                  className="w-[100px] shrink-0 pt-7 pr-4 text-right"
                >
                  <span
                    className="text-sm font-semibold"
                    style={{
                      fontFamily: mono,
                      color: isClosing ? "#d1d5db" : "#FE3A80",
                    }}
                  >
                    {item.time}
                  </span>
                </div>

                {/* Center: Dot */}
                <div className="relative w-[40px] shrink-0 flex justify-center">
                  <div
                    className="relative z-10 shrink-0 flex items-center justify-center rounded-full transition-all duration-300"
                    style={{
                      width: highlight ? 38 : isBreak ? 30 : 32,
                      height: highlight ? 38 : isBreak ? 30 : 32,
                      marginTop: highlight ? 18 : 20,
                      background: highlight
                        ? "#FE3A80"
                        : isBreak
                          ? "#f3f4f6"
                          : isClosing
                            ? "#f3f4f6"
                            : "white",
                      border: highlight
                        ? "3px solid #FE3A80"
                        : isBreak
                          ? "2px dashed #d1d5db"
                          : isClosing
                            ? "2px solid #e5e7eb"
                            : "2px solid #FE3A80",
                      boxShadow: highlight ? "0 0 0 4px rgba(254, 58, 128, 0.12)" : "none",
                    }}
                  >
                    <span
                      className="text-xs font-bold"
                      style={{
                        fontFamily: mono,
                        color: highlight
                          ? "white"
                          : isBreak || isClosing
                            ? "#9ca3af"
                            : "#FE3A80",
                        fontSize: highlight ? 13 : 11,
                      }}
                    >
                      {i + 1}
                    </span>
                  </div>
                </div>

                {/* Right: Card */}
                <div className="flex-1 py-3 pl-4">
                  <div
                    className="relative rounded-lg border overflow-hidden transition-all duration-300 group-hover:shadow-md"
                    style={{
                      background: highlight
                        ? "white"
                        : isBreak
                          ? "#f9fafb"
                          : "white",
                      borderColor: highlight ? "#FE3A80" : "#e5e7eb",
                      borderLeftWidth: highlight ? 3 : 1,
                      borderStyle: isBreak ? "dashed" : "solid",
                    }}
                  >
                    <div className="p-5 md:p-6">
                      <div className="flex items-center gap-3 mb-2">
                        <h3
                          className="text-base md:text-lg"
                          style={{
                            fontFamily: heading,
                            fontWeight: highlight ? 800 : 700,
                            color: isClosing ? "#9ca3af" : isBreak ? "#6b7280" : "#1f2937",
                          }}
                        >
                          {item.title}
                        </h3>
                        {item.duration && (
                          <span
                            className="inline-flex items-center text-[10px] font-medium rounded-full px-2.5 py-0.5 shrink-0"
                            style={{
                              fontFamily: mono,
                              background: highlight ? "#fef2f6" : "#f3f4f6",
                              color: highlight ? "#FE3A80" : "#9ca3af",
                            }}
                          >
                            {item.duration}
                          </span>
                        )}
                      </div>
                      <p
                        className="text-sm leading-relaxed"
                        style={{
                          fontFamily: sans,
                          color: isClosing ? "#d1d5db" : "#6b7280",
                        }}
                      >
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Mobile Timeline ── */}
        <div className="md:hidden relative">
          {/* Left vertical line */}
          <div
            className="absolute top-0 bottom-0 w-[2px]"
            style={{
              left: 15,
              background: "linear-gradient(to bottom, #FE3A80 0%, #FE3A80 70%, #e5e7eb 100%)",
            }}
          />

          {timelineItems.map((item, i) => {
            const highlight = isHighlight(item.type);
            const isBreak = item.type === "break";
            const isClosing = item.type === "closing";

            return (
              <div
                key={i}
                className="relative flex items-start gap-4"
                style={{
                  paddingBottom: i < timelineItems.length - 1 ? 16 : 0,
                }}
              >
                {/* Dot */}
                <div
                  className="relative z-10 shrink-0 flex items-center justify-center rounded-full"
                  style={{
                    width: highlight ? 32 : 28,
                    height: highlight ? 32 : 28,
                    marginTop: 12,
                    marginLeft: highlight ? -1 : 1,
                    background: highlight
                      ? "#FE3A80"
                      : isBreak || isClosing
                        ? "#f3f4f6"
                        : "white",
                    border: highlight
                      ? "3px solid #FE3A80"
                      : isBreak
                        ? "2px dashed #d1d5db"
                        : isClosing
                          ? "2px solid #e5e7eb"
                          : "2px solid #FE3A80",
                    boxShadow: highlight ? "0 0 0 3px rgba(254, 58, 128, 0.12)" : "none",
                  }}
                >
                  <span
                    className="text-[10px] font-bold"
                    style={{
                      fontFamily: mono,
                      color: highlight ? "white" : isBreak || isClosing ? "#9ca3af" : "#FE3A80",
                    }}
                  >
                    {i + 1}
                  </span>
                </div>

                {/* Card */}
                <div className="flex-1 min-w-0">
                  <div
                    className="rounded-lg border overflow-hidden"
                    style={{
                      background: highlight ? "white" : isBreak ? "#f9fafb" : "white",
                      borderColor: highlight ? "#FE3A80" : "#e5e7eb",
                      borderLeftWidth: highlight ? 3 : 1,
                      borderStyle: isBreak ? "dashed" : "solid",
                    }}
                  >
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span
                          className="text-xs font-semibold shrink-0"
                          style={{
                            fontFamily: mono,
                            color: isClosing ? "#d1d5db" : "#FE3A80",
                          }}
                        >
                          {item.time}
                        </span>
                        {item.duration && (
                          <span
                            className="text-[9px] font-medium rounded-full px-2 py-0.5"
                            style={{
                              fontFamily: mono,
                              background: highlight ? "#fef2f6" : "#f3f4f6",
                              color: highlight ? "#FE3A80" : "#9ca3af",
                            }}
                          >
                            {item.duration}
                          </span>
                        )}
                      </div>
                      <h3
                        className="text-sm mb-1"
                        style={{
                          fontFamily: heading,
                          fontWeight: highlight ? 800 : 700,
                          color: isClosing ? "#9ca3af" : isBreak ? "#6b7280" : "#1f2937",
                        }}
                      >
                        {item.title}
                      </h3>
                      <p
                        className="text-xs leading-relaxed"
                        style={{
                          fontFamily: sans,
                          color: isClosing ? "#d1d5db" : "#6b7280",
                        }}
                      >
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
